import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Star, Image as ImageIcon, Eye, Loader2, GripVertical, Save, FileText, DollarSign, Tag, CalendarRange, AlertTriangle } from "lucide-react";
import { api, extractErr } from "@/portal/PortalAuth";
import { btn, ghost, card, inp } from "@/portal/PortalShell";
import { PriceBreakdown } from "@/components/PriceBreakdown";
import { AirbnbConnect } from "@/portal/AirbnbConnect";

const SECTIONS = ["Basics", "Photos", "Pricing", "Discounts", "Availability", "Airbnb", "Preview"];
const DISCOUNT_KINDS = [
  { k: "last_minute", label: "Last-minute (books within N days)" },
  { k: "early_bird", label: "Early-bird (books ≥ N days ahead)" },
  { k: "weekly", label: "Weekly (≥ 7 nights)" },
  { k: "monthly", label: "Monthly (≥ 28 nights)" },
  { k: "custom", label: "Custom promotion" },
  { k: "date_specific", label: "Date-specific" },
];
const TIERS = ["Shared Bath", "Private Bath", "Premium Ensuite", "Studio Loft", "Luxury Home"];

export default function Listings({ initialEditId = null, initialTab = "Basics" }) {
  const [rooms, setRooms] = useState([]);
  const [props, setProps] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editingId, setEditingId] = useState(initialEditId);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([api.get("/admin/booking/properties"), api.get("/admin/booking/rooms")]);
      setProps(p.data); setRooms(r.data);
    } catch (e) { toast.error(extractErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = rooms.filter((r) =>
    filter === "all" ? true : filter === "published" ? (r.status || "published") === "published" :
    filter === "draft" ? r.status === "draft" : r.active === false
  );

  return (
    <div className="space-y-4" data-testid="listings-panel">
      <div className={card}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Listings</p>
            <p className="mt-1 text-sm text-[#78716C]">{rooms.length} room/home listing{rooms.length === 1 ? "" : "s"} across {props.length} propert{props.length === 1 ? "y" : "ies"}.</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="listings-filter" className={inp + " max-w-[180px] h-9"}>
              <option value="all">{`All (${rooms.length})`}</option>
              <option value="published">Published</option>
              <option value="draft">Drafts</option>
              <option value="inactive">Inactive</option>
            </select>
            <button onClick={load} className={ghost + " text-xs"}>{loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Refresh"}</button>
          </div>
        </div>
      </div>

      <FeeSettings />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="listings-grid">
        {filtered.map((r) => {
          const cover = (r.photos || []).find((p) => p.is_cover) || (r.photos || [])[0];
          return (
            <button key={r.id} onClick={() => setEditingId(r.id)} data-testid={`listing-${r.slug}`}
              className="text-left rounded-2xl border border-[#E7E5E4] bg-white overflow-hidden hover:border-[#8A9A86] transition-colors">
              <div className="aspect-[4/3] bg-[#F5F5F4] relative">
                {cover ? <img src={mediaSrc(cover.url)} alt={r.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-[#8A9A86]"><ImageIcon className="w-7 h-7" strokeWidth={1.3} /></div>}
                <span className={`absolute top-2 left-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${(r.status || "published") === "published" ? "bg-[#E8EFE7] text-[#4A6A48]" : "bg-[#FEF3C7] text-[#92400E]"}`}>
                  {(r.status || "published") === "draft" ? "DRAFT" : r.active === false ? "INACTIVE" : "LIVE"}
                </span>
              </div>
              <div className="p-4">
                <p className="text-xs text-[#8A9A86] tracking-[0.18em] uppercase">{r.tier || "—"}</p>
                <p className="mt-1 text-[#1C1917] font-medium">{r.name}</p>
                <p className="mt-2 text-xs text-[#78716C]">
                  ${r.nightly_rate}/night · ${r.monthly_rate}/mo · {r.min_nights}+ nights · {r.max_guests} guests
                </p>
                <p className="mt-1 text-xs text-[#78716C]">
                  {(r.photos || []).length} photo{(r.photos || []).length === 1 ? "" : "s"} · {(r.discounts || []).filter((d) => d.enabled).length} active discount{(r.discounts || []).filter((d) => d.enabled).length === 1 ? "" : "s"}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {editingId && rooms.some((r) => r.id === editingId) && (
        <ListingEditor
          room={rooms.find((r) => r.id === editingId)}
          properties={props}
          initialTab={initialTab}
          onClose={() => setEditingId(null)}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
}

function FeeSettings() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const fromSettings = (s) => ({
    furnished_room_cleaning: s?.fees?.furnished_room?.cleaning_fee ?? 0,
    furnished_room_mgmt_pct: s?.fees?.furnished_room?.management_fee_pct ?? 0,
    luxury_home_cleaning: s?.fees?.luxury_home?.cleaning_fee ?? 0,
    luxury_home_mgmt_pct: s?.fees?.luxury_home?.management_fee_pct ?? 0,
    furnished_room_deposit: s?.fees?.furnished_room?.security_deposit ?? 0,
    luxury_home_deposit: s?.fees?.luxury_home?.security_deposit ?? 0,
  });
  useEffect(() => {
    api.get("/admin/booking/payment-settings").then(({ data }) => setF(fromSettings(data))).catch((e) => toast.error(extractErr(e)));
  }, []);
  if (!f) return null;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, Number(v)]));
      const { data } = await api.put("/admin/booking/payment-settings/fees", body);
      setF(fromSettings(data));
      toast.success("Fees saved. Locked reservations keep their original charges.");
    } catch (e) { toast.error(extractErr(e)); } finally { setBusy(false); }
  };
  const num = (k, testid) => (
    <input type="number" min={0} step="0.01" value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={testid} className={inp + " h-9"} />
  );
  return (
    <div className={card} data-testid="fee-settings">
      <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Fees by category</p>
      <p className="mt-1 text-xs text-[#78716C]">Management fee = % of the discounted accommodation subtotal (excludes cleaning, taxes and deposits), charged with each monthly payment. Direct-booking policy: minimum 30 consecutive nights.</p>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <p className="text-sm text-[#1C1917] font-medium">Furnished Rooms</p>
          <Row><Field label="Cleaning fee ($ / reservation)">{num("furnished_room_cleaning", "fee-furnished-cleaning")}</Field><Field label="Management fee (%)">{num("furnished_room_mgmt_pct", "fee-furnished-mgmt")}</Field></Row>
          <Field label="Refundable security deposit ($, 0 = not charged)">{num("furnished_room_deposit", "fee-furnished-deposit")}</Field>
        </div>
        <div className="space-y-2">
          <p className="text-sm text-[#1C1917] font-medium">Entire Vacation Homes</p>
          <Row><Field label="Cleaning fee ($ / reservation)">{num("luxury_home_cleaning", "fee-luxury-cleaning")}</Field><Field label="Management fee (%)">{num("luxury_home_mgmt_pct", "fee-luxury-mgmt")}</Field></Row>
          <Field label="Refundable security deposit ($, 0 = not charged)">{num("luxury_home_deposit", "fee-luxury-deposit")}</Field>
        </div>
      </div>
      <button onClick={save} disabled={busy} data-testid="fee-save" className={btn + " mt-4 text-xs"}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save fees
      </button>
    </div>
  );
}

function ListingEditor({ room, properties, onClose, onSaved, initialTab }) {
  const [draft, setDraft] = useState(() => normalize(room));
  const [tab, setTab] = useState(initialTab || "Basics");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const save = async () => {
    if (!draft.name || !draft.slug) { toast.error("Name and slug are required."); return; }
    setBusy(true);
    try {
      // Normalize: ensure one cover
      const photos = (draft.photos || []).map((p, i) => ({ ...p, order: p.order ?? i }));
      if (photos.length && !photos.some((p) => p.is_cover)) photos[0].is_cover = true;
      const payload = { ...draft, photos };
      await api.patch(`/admin/booking/rooms/${room.id}`, payload);
      toast.success(`Saved · status ${draft.status.toUpperCase()}`);
      onSaved();
    } catch (e) { toast.error(extractErr(e)); } finally { setBusy(false); }
  };

  const runPreview = async (ci, co) => {
    try {
      const { data } = await api.get(`/admin/booking/rooms/${room.id}/price-preview`, { params: { check_in: ci, check_out: co } });
      setPreview(data);
    } catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-[#1C1917]/40 z-40" onClick={busy ? undefined : onClose} />
      <aside className="fixed inset-y-0 right-0 w-full lg:w-[720px] bg-white z-50 shadow-2xl overflow-y-auto" data-testid="listing-editor">
        <div className="sticky top-0 bg-white border-b border-[#E7E5E4] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86] font-medium">Listing · {draft.status.toUpperCase()}</p>
            <h2 className="mt-0.5 text-lg font-normal tracking-tight text-[#1C1917]">{draft.name || "Untitled"}</h2>
          </div>
          <div className="flex items-center gap-2">
            <select value={draft.status} onChange={(e) => set({ status: e.target.value })} data-testid="editor-status" className={inp + " max-w-[140px] h-9"}>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
            <button onClick={save} disabled={busy} data-testid="editor-save" className={btn + " text-xs"}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save</button>
            <button onClick={onClose} aria-label="Close" className="p-2 text-[#44403C] hover:text-[#1C1917]"><X className="w-5 h-5" strokeWidth={1.5} /></button>
          </div>
        </div>
        <div className="px-6 pt-4 flex gap-1 flex-wrap border-b border-[#E7E5E4]">
          {SECTIONS.map((s) => (
            <button key={s} onClick={() => setTab(s)} data-testid={`editor-tab-${s.toLowerCase()}`}
              className={`px-3 py-2 text-sm border-b-2 transition-colors ${tab === s ? "border-[#1C1917] text-[#1C1917] font-medium" : "border-transparent text-[#78716C]"}`}>{s}</button>
          ))}
        </div>
        <div className="px-6 py-5 pb-24 space-y-4">
          {tab === "Basics" && <Basics draft={draft} set={set} properties={properties} />}
          {tab === "Photos" && <Photos roomId={room.id} draft={draft} set={set} />}
          {tab === "Pricing" && <Pricing draft={draft} set={set} runPreview={runPreview} preview={preview} />}
          {tab === "Discounts" && <Discounts draft={draft} set={set} />}
          {tab === "Availability" && <Availability roomId={room.id} />}
          {tab === "Airbnb" && <AirbnbConnect room={room} onChanged={onSaved} />}
          {tab === "Preview" && <PublicPreview draft={draft} />}
        </div>
      </aside>
    </>
  );
}

// --- Basics ---
function Basics({ draft, set, properties }) {
  return (
    <div className="space-y-3">
      <Row>
        <Field label="Internal name"><input value={draft.name} onChange={(e) => set({ name: e.target.value })} data-testid="field-name" className={inp} /></Field>
        <Field label="Slug (URL)"><input value={draft.slug} onChange={(e) => set({ slug: e.target.value })} data-testid="field-slug" className={inp} /></Field>
      </Row>
      <Field label="Public listing title"><input value={draft.title} onChange={(e) => set({ title: e.target.value })} data-testid="field-title" className={inp} placeholder="Shown on website" /></Field>
      <Field label="Description">
        <textarea rows={4} value={draft.description} onChange={(e) => set({ description: e.target.value })} data-testid="field-description"
          className="w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] focus:border-[#8A9A86] px-3 py-2 text-sm" />
      </Field>
      <Row>
        <Field label="Category / tier">
          <select value={draft.tier} onChange={(e) => set({ tier: e.target.value })} data-testid="field-tier" className={inp}>
            <option value="">—</option>{TIERS.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Property">
          <select value={draft.property_id} onChange={(e) => set({ property_id: e.target.value })} data-testid="field-property" className={inp}>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      </Row>
      <Row>
        <Field label="Max guests"><input type="number" min={1} value={draft.max_guests} onChange={(e) => set({ max_guests: Number(e.target.value) || 1 })} data-testid="field-max-guests" className={inp} /></Field>
        <Field label="Min nights (direct policy minimum 30 applies)"><input type="number" min={1} value={draft.min_nights} onChange={(e) => set({ min_nights: Number(e.target.value) || 1 })} data-testid="field-min-nights" className={inp} /></Field>
        <Field label="Max nights"><input type="number" min={1} value={draft.max_nights} onChange={(e) => set({ max_nights: Number(e.target.value) || 365 })} data-testid="field-max-nights" className={inp} /></Field>
      </Row>
      <Row>
        <Field label="Bedrooms (whole homes, blank for rooms)"><input type="number" min={0} value={draft.bedrooms ?? ""} onChange={(e) => set({ bedrooms: e.target.value === "" ? null : Number(e.target.value) })} data-testid="field-bedrooms" className={inp} /></Field>
        <Field label="Bathrooms (whole homes)"><input type="number" min={0} step="0.5" value={draft.bathrooms ?? ""} onChange={(e) => set({ bathrooms: e.target.value === "" ? null : Number(e.target.value) })} data-testid="field-bathrooms" className={inp} /></Field>
        <Field label="Square footage (optional)"><input type="number" min={0} value={draft.sqft ?? ""} onChange={(e) => set({ sqft: e.target.value === "" ? null : Number(e.target.value) })} data-testid="field-sqft" className={inp} /></Field>
      </Row>
      <Row>
        <Field label="Cleaning fee override ($, blank = category default)"><input type="number" min={0} value={draft.cleaning_fee ?? ""} onChange={(e) => set({ cleaning_fee: e.target.value === "" ? null : Number(e.target.value) })} data-testid="field-cleaning-fee" className={inp} /></Field>
        <Field label="Management fee override (%, blank = category default)"><input type="number" min={0} max={100} value={draft.management_fee_pct ?? ""} onChange={(e) => set({ management_fee_pct: e.target.value === "" ? null : Number(e.target.value) })} data-testid="field-mgmt-fee" className={inp} /></Field>
      </Row>
      <Field label="Listing address (optional)"><input value={draft.address} onChange={(e) => set({ address: e.target.value })} data-testid="field-address" className={inp} /></Field>
      <ListField label="Amenities" items={draft.amenities} onChange={(amenities) => set({ amenities })} testid="field-amenities" placeholder="Wi-Fi, kitchen access…" />
      <ListField label="House rules" items={draft.house_rules} onChange={(house_rules) => set({ house_rules })} testid="field-rules" placeholder="No smoking, quiet hours…" />
    </div>
  );
}

// --- Photos ---
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const mediaSrc = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function Photos({ roomId, draft, set }) {
  const photos = draft.photos || [];
  const [uploading, setUploading] = useState(0);
  const addFiles = async (files) => {
    if (!roomId) { toast.error("Save the listing first, then upload photos."); return; }
    let list = [...photos];
    const valid = Array.from(files).filter((f) => {
      if (!PHOTO_TYPES.includes(f.type)) { toast.error(`${f.name}: only JPEG, PNG or WEBP.`); return false; }
      if (f.size > MAX_PHOTO_BYTES) { toast.error(`${f.name} is larger than 10 MB.`); return false; }
      return true;
    });
    setUploading(valid.length);
    for (const f of valid) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/admin/booking/rooms/${roomId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        // The server already added the photo to the room's public gallery; mirror its list so a later Save can't drop it.
        if (Array.isArray(data.photos)) list = data.photos.map((p) => ({ ...p }));
        else list.push({ url: data.url, caption: "", is_cover: false, order: list.length });
      } catch (e) { toast.error(`${f.name}: ${extractErr(e)}`); }
      setUploading((n) => n - 1);
    }
    if (list.length && !list.some((p) => p.is_cover)) list[0].is_cover = true;
    set({ photos: list });
    if (valid.length) toast.success("Photos uploaded. Press Save to publish the gallery changes.");
  };
  const setCover = (i) => set({ photos: photos.map((p, idx) => ({ ...p, is_cover: idx === i })) });
  const remove = (i) => {
    if (!window.confirm("Remove this photo?")) return;
    const list = photos.filter((_, idx) => idx !== i);
    if (list.length && !list.some((p) => p.is_cover)) list[0].is_cover = true;
    set({ photos: list });
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= photos.length) return;
    const list = [...photos];
    [list[i], list[j]] = [list[j], list[i]];
    set({ photos: list.map((p, idx) => ({ ...p, order: idx })) });
  };
  const caption = (i, caption) => set({ photos: photos.map((p, idx) => (idx === i ? { ...p, caption } : p)) });

  return (
    <div className="space-y-4">
      <label className={ghost + " text-xs cursor-pointer inline-flex"} data-testid="photo-upload">
        {uploading > 0 ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} {uploading > 0 ? `Uploading ${uploading}…` : "Upload photos"}
        <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" disabled={uploading > 0} data-testid="photo-upload-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      </label>
      <p className="text-xs text-[#78716C]">JPEG, PNG or WEBP up to 10 MB each, stored securely and shown on the public booking page as soon as they upload. The photo marked ★ is the cover shown on the booking card; use ↑ ↓ to reorder, add captions (shown under the gallery photo) or Remove, then Save to publish those changes.</p>
      {photos.length === 0 && (
        <div className="rounded-xl border border-dashed border-[#E7E5E4] bg-[#FAFAF9] p-8 text-center text-sm text-[#78716C]">No photos yet. Add at least one to publish.</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {photos.map((p, i) => (
          <div key={i} className={card + " p-3"} data-testid={`photo-${i}`}>
            <div className="aspect-video rounded-lg overflow-hidden bg-[#F5F5F4] relative">
              <img src={mediaSrc(p.url)} alt={p.caption || `Photo ${i + 1}`} className="w-full h-full object-cover" />
              {p.is_cover && <span className="absolute top-2 left-2 text-[10px] uppercase tracking-widest bg-[#1C1917] text-white px-2 py-0.5 rounded inline-flex items-center gap-1"><Star className="w-3 h-3" fill="currentColor" strokeWidth={0} /> Cover</span>}
            </div>
            <input placeholder="Caption shown under this photo on the public gallery (optional)" maxLength={140} value={p.caption || ""} onChange={(e) => caption(i, e.target.value)} data-testid={`photo-caption-${i}`} className={inp + " mt-2 h-9 text-xs"} />
            <div className="mt-2 flex items-center justify-between gap-1">
              <div className="flex gap-1">
                <button onClick={() => move(i, -1)} className={ghost + " text-xs h-8 px-2"} data-testid={`photo-up-${i}`}>↑</button>
                <button onClick={() => move(i, 1)} className={ghost + " text-xs h-8 px-2"} data-testid={`photo-down-${i}`}>↓</button>
              </div>
              <div className="flex gap-1">
                {!p.is_cover && <button onClick={() => setCover(i)} className={ghost + " text-xs h-8"} data-testid={`photo-cover-${i}`}><Star className="w-3 h-3" /> Cover</button>}
                <button onClick={() => remove(i)} className="rounded-full h-8 px-3 text-xs text-red-600 border border-[#E7E5E4] hover:bg-red-50 inline-flex items-center gap-1" data-testid={`photo-remove-${i}`}><Trash2 className="w-3 h-3" /> Remove</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Pricing ---
function Pricing({ draft, set, runPreview, preview }) {
  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const [ci, setCi] = useState(today);
  const [co, setCo] = useState(in30);
  const addSeasonal = () => set({ seasonal_rates: [...(draft.seasonal_rates || []), { start: today, end: in30, nightly: draft.nightly_rate }] });
  const removeSeasonal = (i) => set({ seasonal_rates: draft.seasonal_rates.filter((_, idx) => idx !== i) });
  const updSeasonal = (i, k, v) => set({ seasonal_rates: draft.seasonal_rates.map((s, idx) => idx === i ? { ...s, [k]: k === "nightly" ? Number(v) || 0 : v } : s) });
  const addDate = () => set({ date_rates: [...(draft.date_rates || []), { date: today, nightly: draft.nightly_rate }] });
  const removeDate = (i) => set({ date_rates: draft.date_rates.filter((_, idx) => idx !== i) });
  const updDate = (i, k, v) => set({ date_rates: draft.date_rates.map((d, idx) => idx === i ? { ...d, [k]: k === "nightly" ? Number(v) || 0 : v } : d) });

  return (
    <div className="space-y-4">
      <Row>
        <Field label="Nightly ($)"><input type="number" value={draft.nightly_rate} onChange={(e) => set({ nightly_rate: Number(e.target.value) || 0 })} data-testid="price-nightly" className={inp} /></Field>
        <Field label="Weekly ($)"><input type="number" value={draft.weekly_rate} onChange={(e) => set({ weekly_rate: Number(e.target.value) || 0 })} data-testid="price-weekly" className={inp} /></Field>
        <Field label="Monthly ($)"><input type="number" value={draft.monthly_rate} onChange={(e) => set({ monthly_rate: Number(e.target.value) || 0 })} data-testid="price-monthly" className={inp} /></Field>
      </Row>
      <Field label="Weekend adjustment % (Fri + Sat)"><input type="number" value={draft.weekend_adjustment_pct} onChange={(e) => set({ weekend_adjustment_pct: Number(e.target.value) || 0 })} data-testid="price-weekend" className={inp} /></Field>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Seasonal rates</p>
          <button onClick={addSeasonal} className={ghost + " text-xs"} data-testid="add-seasonal"><Plus className="w-3 h-3" /> Add season</button>
        </div>
        {(draft.seasonal_rates || []).map((s, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 mb-2" data-testid={`seasonal-${i}`}>
            <input type="date" value={s.start} onChange={(e) => updSeasonal(i, "start", e.target.value)} className={inp + " h-9"} />
            <input type="date" value={s.end} onChange={(e) => updSeasonal(i, "end", e.target.value)} className={inp + " h-9"} />
            <input type="number" placeholder="Nightly $" value={s.nightly} onChange={(e) => updSeasonal(i, "nightly", e.target.value)} className={inp + " h-9"} />
            <button onClick={() => removeSeasonal(i)} className="rounded-full h-9 px-3 text-xs text-red-600 border border-[#E7E5E4]"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Date-specific overrides</p>
          <button onClick={addDate} className={ghost + " text-xs"} data-testid="add-date-rate"><Plus className="w-3 h-3" /> Add date</button>
        </div>
        {(draft.date_rates || []).map((d, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2" data-testid={`date-rate-${i}`}>
            <input type="date" value={d.date} onChange={(e) => updDate(i, "date", e.target.value)} className={inp + " h-9"} />
            <input type="number" placeholder="Nightly $" value={d.nightly} onChange={(e) => updDate(i, "nightly", e.target.value)} className={inp + " h-9"} />
            <button onClick={() => removeDate(i)} className="rounded-full h-9 px-3 text-xs text-red-600 border border-[#E7E5E4]"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>

      <div className={card} data-testid="price-preview-box">
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Price preview (saved values)</p>
        <div className="mt-2 grid grid-cols-[1fr_1fr_auto] gap-2">
          <input type="date" value={ci} onChange={(e) => setCi(e.target.value)} data-testid="preview-ci" className={inp + " h-9"} />
          <input type="date" value={co} onChange={(e) => setCo(e.target.value)} data-testid="preview-co" className={inp + " h-9"} />
          <button onClick={() => runPreview(ci, co)} data-testid="preview-run" className={ghost + " text-xs"}>Preview</button>
        </div>
        {preview && (
          <div className="mt-3 text-sm" data-testid="preview-result">
            <p className="text-[#44403C]">{preview.nights} nights · Subtotal <strong className="text-[#1C1917]">${preview.subtotal}</strong>
              {preview.discount_amount > 0 && <> · <span className="text-[#4A6A48]">−${preview.discount_amount} ({preview.discount?.code})</span></>}
              {" "}→ <strong className="text-[#1C1917]">${preview.total}</strong></p>
            {preview.estimate && (
              <div className="mt-3 max-w-md rounded-xl bg-[#FAFAF9] border border-[#E7E5E4] p-3">
                <PriceBreakdown bd={preview.estimate} testidPrefix="listing-preview" totalLabel="Guest total" />
              </div>
            )}
          </div>
        )}
        <p className="mt-2 text-xs text-[#78716C]">Preview uses the room's saved pricing — save your changes above first to see them reflected.</p>
      </div>
    </div>
  );
}

// --- Discounts ---
function Discounts({ draft, set }) {
  const list = draft.discounts || [];
  const add = () => set({ discounts: [...list, { code: `promo-${list.length + 1}`, kind: "custom", enabled: true, value_type: "percent", value: 10, min_nights: 0, valid_from: "", valid_to: "", stackable: false }] });
  const upd = (i, k, v) => set({ discounts: list.map((d, idx) => idx === i ? { ...d, [k]: v } : d) });
  const remove = (i) => {
    if (!window.confirm("Delete this discount?")) return;
    set({ discounts: list.filter((_, idx) => idx !== i) });
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#78716C]">
          By default only the highest eligible discount applies at checkout (no stacking).
          Toggle Stackable per discount to allow combining with others.
        </p>
        <button onClick={add} className={ghost + " text-xs"} data-testid="add-discount"><Plus className="w-3 h-3" /> Add discount</button>
      </div>
      {list.length === 0 && <p className="text-sm text-[#78716C]">No discounts.</p>}
      {list.map((d, i) => (
        <div key={i} className={card} data-testid={`discount-${i}`}>
          <div className="flex items-center justify-between mb-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={d.enabled} onChange={(e) => upd(i, "enabled", e.target.checked)} data-testid={`discount-enabled-${i}`} className="h-4 w-4 accent-[#1C1917]" />
              <span className="font-medium text-[#1C1917]">{d.code}</span>
            </label>
            <button onClick={() => remove(i)} className="text-red-600 text-xs inline-flex items-center gap-1" data-testid={`discount-remove-${i}`}><Trash2 className="w-3 h-3" /> Remove</button>
          </div>
          <Row>
            <Field label="Code"><input value={d.code} onChange={(e) => upd(i, "code", e.target.value)} className={inp + " h-9"} /></Field>
            <Field label="Kind">
              <select value={d.kind} onChange={(e) => upd(i, "kind", e.target.value)} className={inp + " h-9"}>
                {DISCOUNT_KINDS.map((k) => <option key={k.k} value={k.k}>{k.label}</option>)}
              </select>
            </Field>
          </Row>
          <Row>
            <Field label="Type">
              <select value={d.value_type} onChange={(e) => upd(i, "value_type", e.target.value)} className={inp + " h-9"}>
                <option value="percent">Percent %</option><option value="fixed">Fixed $</option>
              </select>
            </Field>
            <Field label="Value"><input type="number" value={d.value} onChange={(e) => upd(i, "value", Number(e.target.value) || 0)} className={inp + " h-9"} /></Field>
            <Field label="Min nights / lead days"><input type="number" value={d.min_nights} onChange={(e) => upd(i, "min_nights", Number(e.target.value) || 0)} className={inp + " h-9"} /></Field>
          </Row>
          <Row>
            <Field label="Valid from"><input type="date" value={d.valid_from || ""} onChange={(e) => upd(i, "valid_from", e.target.value)} className={inp + " h-9"} /></Field>
            <Field label="Valid to"><input type="date" value={d.valid_to || ""} onChange={(e) => upd(i, "valid_to", e.target.value)} className={inp + " h-9"} /></Field>
          </Row>
          <label className="inline-flex items-center gap-2 text-xs text-[#44403C] mt-2">
            <input type="checkbox" checked={!!d.stackable} onChange={(e) => upd(i, "stackable", e.target.checked)} className="h-3.5 w-3.5 accent-[#1C1917]" />
            Allow stacking with other discounts
          </label>
        </div>
      ))}
    </div>
  );
}

// --- Availability ---
function Availability({ roomId }) {
  const [blocks, setBlocks] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [summary, setSummary] = useState("Blocked for maintenance");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const now = new Date(); const in1y = new Date(Date.now() + 365 * 86400000);
      const { data } = await api.get("/admin/booking/calendar", { params: { start: now.toISOString().slice(0, 10), end: in1y.toISOString().slice(0, 10), room_id: roomId } });
      setBlocks(data.events);
    } catch (e) { toast.error(extractErr(e)); }
  };
  useEffect(() => { load(); }, [roomId]);
  const add = async () => {
    if (!start || !end) { toast.error("Choose start and end dates."); return; }
    if (end <= start) { toast.error("End must be after start."); return; }
    if (!window.confirm(`Block this room from ${start} to ${end}?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/booking/rooms/${roomId}/manual-block`, { start, end, summary });
      toast.success("Block created."); setStart(""); setEnd(""); load();
    } catch (e) { toast.error(extractErr(e)); } finally { setBusy(false); }
  };
  const removeBlock = async (b) => {
    if (b.source !== "manual") { toast.error("Only manual blocks can be deleted here. Airbnb blocks are read-only; reservations are managed in Reservations tab."); return; }
    if (!window.confirm("Delete this manual block?")) return;
    try { await api.delete(`/admin/booking/manual-block/${b.id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(extractErr(e)); }
  };
  return (
    <div className="space-y-4">
      <div className={card}>
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Add a block</p>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2">
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="block-start" className={inp + " h-9"} />
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="block-end" className={inp + " h-9"} />
          <input value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="block-summary" className={inp + " h-9"} placeholder="Reason (maintenance, long-term…)" />
          <button onClick={add} disabled={busy} data-testid="block-add" className={btn + " text-xs"}>Add block</button>
        </div>
      </div>
      <div className={card}>
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Upcoming blocks &amp; reservations (next 12 months)</p>
        {blocks.length === 0 && <p className="mt-3 text-sm text-[#78716C]">None.</p>}
        <ul className="mt-3 divide-y divide-[#E7E5E4]">
          {blocks.sort((a, b) => a.start.localeCompare(b.start)).map((b, i) => (
            <li key={i} className="py-2 text-sm flex items-center justify-between gap-3" data-testid={`block-row-${i}`}>
              <div>
                <span className={`inline-block w-2 h-2 rounded-full mr-2 ${b.source === "airbnb" ? "bg-[#FF5A5F]" : b.source === "manual" ? "bg-[#78716C]" : "bg-[#8A9A86]"}`} />
                <span className="text-[#1C1917]">{b.start} → {b.end}</span>
                <span className="text-[#78716C] ml-2">· {b.summary}</span>
              </div>
              {b.source === "manual" && (
                <button onClick={() => removeBlock(b)} className="text-red-600 text-xs" data-testid={`block-del-${i}`}>Delete</button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// --- Public preview ---
function PublicPreview({ draft }) {
  const cover = (draft.photos || []).find((p) => p.is_cover) || (draft.photos || [])[0];
  return (
    <div className="rounded-2xl border border-[#E7E5E4] overflow-hidden" data-testid="public-preview">
      <div className="aspect-[16/9] bg-[#F5F5F4]">
        {cover ? <img src={mediaSrc(cover.url)} className="w-full h-full object-cover" alt={draft.name} />
          : <div className="w-full h-full flex items-center justify-center text-[#8A9A86]"><ImageIcon className="w-10 h-10" strokeWidth={1} /></div>}
      </div>
      <div className="p-6 bg-white">
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">{draft.tier || "Furnished unit"}</p>
        <h3 className="mt-2 text-2xl tracking-tight font-light text-[#1C1917]">{draft.title || draft.name}</h3>
        <p className="mt-3 text-sm text-[#44403C] font-light whitespace-pre-wrap">{draft.description || "—"}</p>
        <div className="mt-5 flex items-baseline gap-2">
          <span className="text-xl font-normal text-[#1C1917]">${draft.nightly_rate}</span>
          <span className="text-sm text-[#78716C]">/ night · from ${draft.monthly_rate} / month</span>
        </div>
        {(draft.amenities || []).length > 0 && (
          <div className="mt-5">
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Amenities</p>
            <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-[#1C1917]">
              {draft.amenities.map((a) => <li key={a}>· {a}</li>)}
            </ul>
          </div>
        )}
        {draft.status === "draft" && (
          <div className="mt-6 rounded-lg bg-[#FEF3C7] border border-[#FDE68A] px-3 py-2 text-xs text-[#92400E] flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" /> This listing is a DRAFT — it is not shown on the public website.
          </div>
        )}
      </div>
    </div>
  );
}

// --- helpers ---
function normalize(r) {
  return {
    name: r.name || "", slug: r.slug || "", title: r.title || "",
    description: r.description || "", property_id: r.property_id || "",
    tier: r.tier || "", status: r.status || "published", active: r.active !== false,
    photos: r.photos || [], amenities: r.amenities || [], house_rules: r.house_rules || [],
    nightly_rate: r.nightly_rate || 0, weekly_rate: r.weekly_rate || 0, monthly_rate: r.monthly_rate || 0,
    weekend_adjustment_pct: r.weekend_adjustment_pct || 0,
    seasonal_rates: r.seasonal_rates || [], date_rates: r.date_rates || [],
    discounts: r.discounts || [],
    min_nights: r.min_nights || 1, max_nights: r.max_nights || 365, max_guests: r.max_guests || 2,
    airbnb_listing_id: r.airbnb_listing_id || "", airbnb_url: r.airbnb_url || "",
    address: r.address || "", sync_schedule: r.sync_schedule || "off",
    cleaning_fee: r.cleaning_fee ?? null, management_fee_pct: r.management_fee_pct ?? null,
  };
}
function Row({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>; }
function Field({ label, children }) { return <div><label className="text-xs text-[#78716C] block mb-1">{label}</label>{children}</div>; }
function ListField({ label, items, onChange, testid, placeholder }) {
  const text = (items || []).join("\n");
  return (
    <div>
      <label className="text-xs text-[#78716C] block mb-1">{label} (one per line)</label>
      <textarea rows={3} value={text} onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
        data-testid={testid} placeholder={placeholder}
        className="w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] focus:border-[#8A9A86] px-3 py-2 text-sm" />
    </div>
  );
}
